import React, { Component } from 'react';
import { Text, Flex, Link, Dropdown, Button, TextField } from 'monday-ui-react-core';
import { Heading } from "monday-ui-react-core/next";
import classes from "./settings.module.scss";
import { getSettings, saveSettings } from '../../services/settings-service';
import mondaySdk from "monday-sdk-js";

const APP_NAME = "IMAP";
const APP_PRICING_URL = "https://auth.monday.com/marketplace/listing/10000627/imap-email-connector#pricing";

const monday = mondaySdk();

class Settings extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      consumption: "Loading...",
      timezone: 'UTC',
      timezoneOptions: [],
      subjectFilterRegex: '',
      storedSettings: {},
      isSaving: false
    };
  }

  componentDidMount() {
    const fetchData = async () => {
      let data = await getSettings(APP_NAME);

      const newState = {
        consumption: data.consumption
      };

      const storedSettings = data.settings || {};
      newState.storedSettings = storedSettings;

      // Load stored settings if they exist
      if (storedSettings.timezone) {
        newState.timezone = storedSettings.timezone;
      }

      if (storedSettings.subjectFilterRegex) {
        newState.subjectFilterRegex = storedSettings.subjectFilterRegex;
      }

      this.setState(newState);
    }

    // Generate timezone options with DST-aware timezone identifiers
    const generateTimezoneOptions = () => {
      const timezones = [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Anchorage',
        'America/Honolulu',
        'America/Toronto',
        'America/Vancouver',
        'America/Montreal',
        'America/Mexico_City',
        'America/Sao_Paulo',
        'America/Buenos_Aires',
        'America/Lima',
        'America/Bogota',
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'Europe/Rome',
        'Europe/Madrid',
        'Europe/Amsterdam',
        'Europe/Brussels',
        'Europe/Vienna',
        'Europe/Zurich',
        'Europe/Stockholm',
        'Europe/Oslo',
        'Europe/Copenhagen',
        'Europe/Helsinki',
        'Europe/Warsaw',
        'Europe/Prague',
        'Europe/Budapest',
        'Europe/Bucharest',
        'Europe/Athens',
        'Europe/Istanbul',
        'Europe/Moscow',
        'Asia/Tokyo',
        'Asia/Seoul',
        'Asia/Shanghai',
        'Asia/Beijing',
        'Asia/Hong_Kong',
        'Asia/Singapore',
        'Asia/Bangkok',
        'Asia/Jakarta',
        'Asia/Manila',
        'Asia/Kuala_Lumpur',
        'Asia/Kolkata',
        'Asia/Mumbai',
        'Asia/Delhi',
        'Asia/Karachi',
        'Asia/Dubai',
        'Asia/Tehran',
        'Asia/Baghdad',
        'Asia/Riyadh',
        'Asia/Jerusalem',
        'Africa/Cairo',
        'Africa/Johannesburg',
        'Africa/Lagos',
        'Africa/Nairobi',
        'Africa/Casablanca',
        'Australia/Sydney',
        'Australia/Melbourne',
        'Australia/Brisbane',
        'Australia/Perth',
        'Australia/Adelaide',
        'Australia/Darwin',
        'Pacific/Auckland',
        'Pacific/Fiji',
        'Pacific/Honolulu',
        'Pacific/Guam'
      ];

      const options = timezones.map(tz => ({
        label: tz,
        value: tz
      }));

      this.setState({ timezoneOptions: options });
    };

    fetchData();
    generateTimezoneOptions();
  }

  getFirstDayOfNextMonth() {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const day = String(nextMonth.getDate()).padStart(2, '0');
    const month = nextMonth.toLocaleString('default', { month: 'short' });
    const year = nextMonth.getFullYear();
    return `${day} ${month} ${year}`;
  }

  handleTimezoneChange = (timezone) => {
    if(timezone) {
      this.setState({ timezone: timezone.value });
    }
  }

  handleSubjectFilterRegexChange = (value) => {
    this.setState({ subjectFilterRegex: value });
  }

  handleSave = async () => {
    const settingsToSave = {
      ...this.state.storedSettings,
      timezone: this.state.timezone
    };

    delete settingsToSave.systemSettings;

    const regexValue = this.state.subjectFilterRegex ? this.state.subjectFilterRegex.trim() : '';

    if (regexValue.length > 0) {
      settingsToSave.subjectFilterRegex = regexValue;
    } else {
      delete settingsToSave.subjectFilterRegex;
    }

    this.setState({ isSaving: true });
    
    try {
      await saveSettings(APP_NAME, settingsToSave);
      this.setState({ storedSettings: settingsToSave });
      
      monday.execute("notice", { 
        type: "success", 
        message: "Settings saved successfully!", 
        timeout: 5000 
      });
      
    } catch (error) {
      console.error('Error saving settings:', error);
      monday.execute("notice", { 
        type: "error", 
        message: error.message || "Failed to save settings. Please try again.", 
        timeout: 5000 
      });
    } finally {
      this.setState({ isSaving: false });
    }
  }

  render() {
    return (
      <Flex align={Flex.align.CENTER} gap={Flex.gaps.LARGE} direction={Flex.directions.COLUMN} justify={Flex.justify.START} className={classes.container}>
        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} justify={Flex.justify.START} className={classes.fullWidth}>
          <Heading type={Heading.types.H3}>Settings</Heading>
        </Flex>
        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
          <Text type={Text.types.TEXT2} className={classes.fullWidth}>Select your timezone</Text>
          <Dropdown
            title="Timezone"
            placeholder="Select timezone..."
            options={this.state.timezoneOptions}
            value={this.state.timezoneOptions.find(option => option.value === this.state.timezone)}
            onChange={this.handleTimezoneChange}
            className={classes.fullWidth}
          />
        </Flex>
        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
          <TextField
            size={TextField.sizes.MEDIUM}
            title="Words to remove from e-mail subjects (comma separated)"
            placeholder="Enter a comma separated list of words to remove from e-mail subjects (e.g., 'Re:, Fwd:, etc.)"
            className={classes.fullWidth}
            value={this.state.subjectFilterRegex}
            onChange={this.handleSubjectFilterRegexChange}
            validation={{
              text: 'Contact support@deview-studios.com for guidance on using subject filters.'
            }}
          />
        </Flex>
        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} className={classes.fullWidth}>
          <Button
            onClick={this.handleSave}
            loading={this.state.isSaving}
            disabled={this.state.isSaving}
          >
            {this.state.isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Flex>

        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} justify={Flex.justify.START} className={classes.fullWidth}>
          <Heading type={Heading.types.H3}>Subscription</Heading>
        </Flex>
        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} className={classes.fullWidth}>
          <Text type={Text.types.TEXT2} className={classes.fullWidth}>E-mails for this month</Text>
          <Text type={Text.types.TEXT2} className={classes.fullWidth}>{this.state.consumption}</Text>
        </Flex>
        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} className={classes.fullWidth}>
          <Text type={Text.types.TEXT2} className={classes.fullWidth}>Remaining e-mails for this month</Text>
          <Link text="See plans" href={APP_PRICING_URL} className={classes.fullWidth} />
        </Flex>
        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} className={classes.fullWidth}>
          <Text type={Text.types.TEXT2} className={classes.fullWidth}>Usage resets on</Text>
          <Text type={Text.types.TEXT2} className={classes.fullWidth}>{this.getFirstDayOfNextMonth()}</Text>
        </Flex>
      </Flex>
    );
  }
}

export default Settings;
